import React, { useEffect, useState } from 'react';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import createGenerateClassName from '@mui/styles/createGenerateClassName';
import StylesProvider from '@mui/styles/StylesProvider';

import UIErrorBox from 'client/app/components/UIErrorBox';
import { switchFail } from 'common/assert';
import Colors from 'common/ui/Colors';
import makeStylesHook from 'common/ui/hooks/makeStylesHook';
import makeGlobalStylesHook from 'common/ui/makeGlobalStylesHook';
import { LightTheme, theme } from 'common/ui/theme';

/**
 * Definition of a domain and the method to check if it's reachable
 */
type Domain = {
  /** the URL to check */
  url: string;

  /**
   * Prefer using image over http-get as it's more reliable.
   *
   * **image**: check if url is available using an image tag. Images are loaded even from
   * different origins, and we can use this as a reliable way to check if the URL
   * is accessible. It's unlikely for a firewall to replace image urls with
   * other images so that's covered too.
   *
   * **http-get**: check if url is available using HTTP GET via fetch API. Use it only if ImgCheck
   * can't be used because it's less reliable - GetCheck only verifies if the URL
   * responds with something but not if the response is what was expected.
   * The main issue with fetch is that it's restricted by single origin policy - it's
   * not possible to read the response from other origins so we can't reliably verify
   * that the expected content was returned and we don't have access to the response
   * status code.
   * This leads to another problem - even if we know that the URL was successfully
   * downloaded we can't tell if the response isn't something some page served by
   * customer's firewall saying that the page is missing.
   */
  type: 'image' | 'http-get';

  /**
   * Whether this domain is required only when for customers using *.antha.com
   * Defaults to false.
   */
  onlyAnthaCom?: boolean;
};

function defineDomains(customer: string): Domain[] {
  return [
    {
      url: `https://${customer}.antha.com/app/assets/images/synthace-logo.svg`,
      type: 'image',
      onlyAnthaCom: true,
    },
    {
      url: 'https://auth.antha.com',
      type: 'http-get',
      onlyAnthaCom: true,
    },
    {
      url: 'https://admin.antha.com/favicon.png',
      type: 'image',
      onlyAnthaCom: true,
    },
    {
      url: 'https://downloads.antha.com/conntest.png',
      type: 'image',
      onlyAnthaCom: true,
    },
    {
      url: 'https://docs.antha.com',
      type: 'http-get',
      onlyAnthaCom: true,
    },
    {
      url: `https://${customer}.synthace.bio/app/assets/images/synthace-logo.svg`,
      type: 'image',
    },
    {
      url: 'https://auth.synthace.bio/synthace-logo.svg',
      type: 'image',
    },
    {
      url: 'https://downloads.synthace.bio/conntest.png',
      type: 'image',
    },
    {
      url: 'https://docs.synthace.bio',
      type: 'http-get',
    },
    {
      url: 'https://cdn.synthace.bio/synthace-logo.svg',
      type: 'image',
    },
    {
      url: `https://antha-files-${customer}.storage.googleapis.com`,
      type: 'http-get',
    },
    {
      url: `https://cdn.auth0.com/manhattan/versions/1.3431.0/assets/badge.png`,
      type: 'image',
    },
    {
      url: 'https://cdn.eu.auth0.com',
      type: 'http-get',
    },
    {
      url: 'https://synthace.eu.auth0.com',
      type: 'http-get',
    },
    {
      url: 'https://antha.com/app/assets/images/synthace-logo.svg',
      type: 'image',
    },
  ];
}

function intercomDomains(): string[] {
  return [
    'intercom.io',
    'intercom.help',
    'uploads.intercomcdn.com',
    'js.intercomcdn.com',
    'fonts.intercomcdn.com',
    'downloads.intercomcdn.com',
    'gifs.intercomcdn.com ',
    'video-messages.intercomcdn.com',
    'static.intercomassets.com',
    'www.intercom-reporting.com ',
    'intercom-attachments-1.com',
    'intercom-attachments-2.com',
    'intercom-attachments-3.com',
    'intercom-attachments-4.com',
    'intercom-attachments-5.com',
    'intercom-attachments-6.com',
    'intercom-attachments-7.com',
    'intercom-attachments-8.com',
    'intercom-attachments-9.com',
    'intercom-sheets.com',
  ];
}

/**
 * A simple view which shows whether URLs required for Synthace are
 * accessible. It's separate from the rest of the UI because it needs
 * to run without logging in and without access to any external
 * resources.
 *
 * Supported query parameters:
 * `customer` - name of the customers. If not provided then it defaults to the least significant subdomain (`foo` in foo.bar.baz.com)
 * `showall` - run all checks, as if accessing from *.antha.com. Some checks don't show up on *.synthace.bio.
 */
export function ConnectivityTest() {
  useGlobalStyles();
  const classes = useStyles();
  const params = new URLSearchParams(window.location.search);
  const customer = params.get('customer') || window.location.hostname.split('.')[0];
  const domains = defineDomains(customer);

  const shouldShow = (domain: Domain) =>
    params.has('showall') ||
    !domain.onlyAnthaCom ||
    window.location.hostname.includes('antha.com');

  return (
    <LightTheme>
      <StylesProvider generateClassName={generateClassName} injectFirst>
        <div className={classes.rootOuter}>
          <div className={classes.rootInner}>
            <Typography variant="h1" color="textPrimary">
              Connectivity status
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains
                  .filter(domain => shouldShow(domain))
                  .map(domain => (
                    <ConnectivityIndicator key={domain.url} domain={domain} />
                  ))}
              </TableBody>
            </Table>

            <Typography
              variant="h3"
              color="textPrimary"
              className={classes.intercomTitle}
            >
              Support tools and documentation
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {intercomDomains().map(domain => (
                  <ConnectivityIndicator
                    key={domain}
                    domain={{
                      url: `https://${domain}`,
                      type: 'http-get',
                    }}
                  />
                ))}
              </TableBody>
            </Table>

            <UnreliableStatusWarning />
          </div>
        </div>
      </StylesProvider>
    </LightTheme>
  );
}

enum Status {
  OK,
  IN_PROGRESS,
  ERROR,
}

type Result = {
  status: Status;
  message?: string;
};

/**
 * Displays whether the given domain is reachable.
 */
function ConnectivityIndicator(props: { domain: Domain }) {
  const domain = props.domain;
  const classes = useStyles();
  const [status, setStatus] = useState(Status.IN_PROGRESS);
  const [message, setMessage] = useState<string>();

  useEffect(() => {
    (async () => {
      try {
        const checkFn = domain.type === 'image' ? imageCheck : httpGetWithoutCors;
        const result = await checkFn(domain.url);
        setStatus(result.status);
        setMessage(result.message);
      } catch (e) {
        setStatus(Status.ERROR);
        setMessage(e instanceof Error ? e.message : `Unexpected error: ${e}`);
      }
    })();
  }, [domain.type, domain.url]);

  let statusElement;
  switch (status) {
    case Status.OK:
      statusElement = (
        <TableCell className={classes.checkTd}>
          <CheckCircleOutlineOutlinedIcon
            htmlColor={Colors.SUCCESS_MAIN}
            titleAccess="Success"
            className={classes.statusIcon}
          />
          OK
        </TableCell>
      );
      break;
    case Status.IN_PROGRESS:
      statusElement = (
        <TableCell className={classes.checkTd}>
          <CircularProgress size={13} />
        </TableCell>
      );
      break;
    case Status.ERROR:
      statusElement = (
        <TableCell className={classes.checkTd}>
          <ErrorOutlineOutlinedIcon
            htmlColor={Colors.ERROR_MAIN}
            titleAccess="Error"
            className={classes.statusIcon}
          />
          <Typography variant="inherit" color="textPrimary">
            {message}
          </Typography>
        </TableCell>
      );
      break;
    default:
      switchFail(status);
  }

  return (
    <TableRow>
      <TableCell className={classes.checkTd}>{new URL(domain.url).hostname}</TableCell>
      {statusElement}
    </TableRow>
  );
}

// fetch call with default options fails due to single-origin policy and it's not possible
// to tell if it's because of SOP or because of network error.
// Fortunately when using mode 'no-cors' the behaviour differs - it still throws an error
// on network issues (e.g. host unreachable) but if the response is unavailable due to SOP
// there is no error and response.status is set to 0.
// This allows us to check if the URL is accessible.
async function httpGetWithoutCors(url: string): Promise<Result> {
  const response = await fetch(url, {
    credentials: 'omit',
    cache: 'no-cache',
    mode: 'no-cors',
  });
  if (response.status === 0) {
    console.log(url, 'URL accessible, status=', response.status);
    return {
      status: Status.OK,
    };
  } else {
    // this should never happen
    throw new Error('Unexpected status code: ' + response.status);
  }
}

// Adds <img> to the document and returns information when the image loads
// or fails to load. This function creates an image object because images
// load even from different origins and this allow to reliably check
// whether the URL of the image is accessible.
async function imageCheck(url: string): Promise<Result> {
  const imgReady = new Promise(
    (resolve: (value: void) => void, reject: (reason?: any) => void) => {
      const img = document.createElement('img');
      img.onload = () => {
        resolve();
      };
      img.onerror = (e: Event | string) => {
        console.log('failed to load: ', url, 'event:', e);
        reject(new Error('Unavailable'));
      };
      img.src = url;
    },
  );

  await imgReady;
  return { status: Status.OK };
}

/**
 * Some firewalls can be configured to return a custom page accessing one that isn't available.
 * This makes our GetCheck always successful and incorrect. Detect such situation and show a warning.
 */
function UnreliableStatusWarning() {
  const [isUnreliable, setIsUnreliable] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        // try to fetch from domain which definitely does not exist
        await httpGetWithoutCors(
          'https://doesntexist7ay9d87fas89dfbta896sbtf76atsbdf7b.antha.com',
        );
        setIsUnreliable(true);
      } catch (e) {
        // all fine, this was supposed to fail
      }
    })();
  }, []);

  const classes = useStyles();

  return isUnreliable ? (
    <div className={classes.footer}>
      <UIErrorBox>
        It appears that your firewall configuration returns responses even for unreachable
        addresses - this makes above checks unreliable. Please mention this when
        contacting Synthace.
      </UIErrorBox>
    </div>
  ) : null;
}

const useGlobalStyles = makeGlobalStylesHook({
  html: {
    backgroundColor: Colors.BACKGROUND,
  },
});

const useStyles = makeStylesHook({
  rootOuter: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    overflow: 'auto',
  },
  rootInner: {
    display: 'flex',
    flexDirection: 'column',
  },
  checkTd: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderColor: Colors.GREY_30,
  },
  statusIcon: {
    verticalAlign: 'bottom',
    marginRight: theme.spacing(2),
    fontSize: 'medium',
  },
  intercomTitle: {
    marginTop: theme.spacing(6),
  },
  footer: {
    marginTop: theme.spacing(10),
    maxWidth: '600px',
  },
});

// To avoid class names clashes, customise the behaviour
// for auto-generated class names
const generateClassName = createGenerateClassName({
  productionPrefix: 'conntest-',
});
